/**
 * Finance dashboard (BILLING role) — revenue KPIs computed client-side
 * from the billing list, outstanding invoices and recent payments.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useAuth } from '../../../context/AuthContext';
import { billingAPI } from '../../../services/api';
import Skeleton from '../../../components/common/Skeleton';
import {
  T, FONT, I, firstNameOf, greeting, asList, fmtDate, fmtMoney,
  FadeSection, Hero, KpiCard, ChartCard, ChartTip, EmptyState, StatusBadge,
  ListRow, QuickActions, PolarStyles,
} from '../shared';

const amt = (inv) => Number(inv.total_amount ?? inv.amount ?? 0);
const statusOf = (inv) => String(inv.payment_status || inv.status || '').toUpperCase();

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      billingAPI.list({ limit: 100 }).catch(() => null),
    ]).then(([b]) => {
      if (b.status === 'fulfilled') setInvoices(asList(b.value));
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalBilled = invoices.reduce((s, i) => s + amt(i), 0);
  const collected = invoices.filter(i => statusOf(i) === 'PAID').reduce((s, i) => s + amt(i), 0);
  const outstandingList = invoices.filter(i => ['PENDING', 'OVERDUE', 'PARTIALLY_PAID'].includes(statusOf(i)));
  const outstanding = outstandingList.reduce((s, i) => s + amt(i), 0);
  const insuranceList = invoices.filter(i => statusOf(i) === 'INSURANCE_PROCESSING');
  const insurance = insuranceList.reduce((s, i) => s + amt(i), 0);
  const recentPaid = invoices.filter(i => statusOf(i) === 'PAID').slice(0, 6);

  const split = [
    { name: 'Collected', value: collected, fill: T.green },
    { name: 'Outstanding', value: outstanding, fill: T.warning },
    { name: 'Insurance', value: insurance, fill: T.info },
  ].filter(d => d.value > 0);

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.KPIGrid count={4} cols={4} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const kpis = [
    { title: 'Total Billed', value: totalBilled, displayValue: fmtMoney(totalBilled), color: T.primary, Icon: I.diamond, sub: `${invoices.length} invoices` },
    { title: 'Collected', value: collected, displayValue: fmtMoney(collected), color: T.green, Icon: I.check, sub: 'paid in full' },
    { title: 'Outstanding', value: outstanding, displayValue: fmtMoney(outstanding), color: T.warning, Icon: I.coin, sub: `${outstandingList.length} open invoices` },
    { title: 'Insurance Processing', value: insurance, displayValue: fmtMoney(insurance), color: T.info, Icon: I.shield, sub: `${insuranceList.length} claims` },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Finance & billing"
        title={`${greeting()}, ${firstName} 💼`}
        subtitle="Revenue, collections and outstanding invoices across the organisation."
        stats={[
          { label: 'Open invoices', value: outstandingList.length, Icon: I.coin },
          { label: 'Total invoices', value: invoices.length, Icon: I.file },
        ]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      <FadeSection index={2} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
        <ChartCard title="Revenue Split" subtitle="Collected vs outstanding vs insurance">
          {split.length ? (
            <>
              <div style={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={split} cx="50%" cy="50%"
                      innerRadius="62%" outerRadius="92%"
                      dataKey="value" nameKey="name"
                      paddingAngle={2} cornerRadius={6} stroke="none"
                    >
                      {split.map((d, i) => <Cell key={i} fill={d.fill} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: T.text, lineHeight: 1 }}>{fmtMoney(totalBilled)}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '.6px', marginTop: 4 }}>billed</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                {split.map((d, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: T.text, background: T.pageBg, border: `1px solid ${T.border}`, borderRadius: 999, padding: '4px 10px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.fill }} />
                    {d.name}
                    <span style={{ color: T.muted, fontWeight: 700 }}>{fmtMoney(d.value)}</span>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <EmptyState icon={I.chart} color={T.primary} title="No billing data yet" caption="The revenue split will appear once invoices are issued." />
          )}
        </ChartCard>

        <ChartCard
          title="Outstanding Invoices"
          subtitle="Pending, overdue and partially paid"
          action={<button onClick={() => navigate('/billing')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.warning, fontFamily: FONT }}>View all →</button>}
        >
          {outstandingList.length ? outstandingList.slice(0, 6).map((inv, i) => (
            <ListRow
              key={inv.invoice_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.warning}12` }}><I.coin size={16} color={T.warning} /></span>}
              title={`${inv.invoice_number ? `Invoice ${inv.invoice_number}` : `Invoice #${inv.invoice_id ?? i + 1}`} · ${fmtMoney(amt(inv))}`}
              sub={[inv.patient_name, inv.due_date ? `Due ${fmtDate(inv.due_date)}` : null].filter(Boolean).join(' · ') || null}
              right={<StatusBadge value={statusOf(inv)} />}
            />
          )) : (
            <EmptyState icon={I.check} color={T.green} title="Nothing outstanding" caption="All invoices are settled — outstanding items will appear here." />
          )}
        </ChartCard>

        <ChartCard title="Recent Payments" subtitle="Latest invoices marked paid">
          {recentPaid.length ? recentPaid.map((inv, i) => (
            <ListRow
              key={inv.invoice_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.green}12` }}><I.check size={16} color={T.green} /></span>}
              title={fmtMoney(amt(inv))}
              sub={[inv.patient_name, inv.invoice_number ? `Invoice ${inv.invoice_number}` : null].filter(Boolean).join(' · ') || null}
              right={<StatusBadge value="PAID" />}
            />
          )) : (
            <EmptyState icon={I.coin} color={T.green} title="No payments yet" caption="Paid invoices will show up here as collections come in." />
          )}
        </ChartCard>
      </FadeSection>

      <FadeSection index={3}>
        <QuickActions
          navigate={navigate}
          actions={[
            { label: 'Billing',       path: '/billing',       color: T.warning, Icon: I.coin },
            { label: 'Analytics',     path: '/analytics',     color: T.info,    Icon: I.chart },
            { label: 'Care Requests', path: '/care-requests', color: T.primary, Icon: I.request },
          ]}
        />
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
